import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useDrag } from 'react-dnd';
import Button from '../../misc/Button';

import FieldTypes from '../../../constants/FieldTypes';
import FieldDisplayTypes from '../../../constants/FieldDisplayTypes';
import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';

import { StyledFieldLabel } from './SettingsCommonElements';
import OptionSettingsModal from './OptionSettingsModal';
import Icon from '../../misc/Icon';

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledLabelWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 6px; 
`;

const StyledLabel = styled(StyledFieldLabel)`
  flex: 1;
  white-space: nowrap;
  margin-bottom: 0px;
`;

const StyledAddWrapper = styled.div`
  flex: 1;
  transition: all 0.15s ease-in-out;
`;

const StyledOptionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: ${(props) => props.theme.borderRadius}px;
  border: 1px solid ${(props) => props.theme.borderColor};
  padding: 6px;
`;

const StyledOption = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  border-radius: ${(props) => props.theme.borderRadius}px;
  border: 1px solid ${(props) => (props.active ? props.theme.primaryColor : props.theme.borderColor)};
  color: ${(props) => props.theme.fontColorPrimary};

  padding: 6px;
  margin-bottom: 4px;
  :hover {
    ${(props) => (props.isSelectorType ? 'cursor: grab;' : '')}
    svg.fa-grip-vertical {
      ${(props) => (props.isSelectorType ? `color: ${props.theme.primaryColor};` : '')}
    }
  };

`;

const StyledOptionLabel = styled.div`
  display: flex;
  flex: 1;
  margin-right: 8px;
  font-size: 12px;
  max-width: 100%;
`;

const StyledOptionDragIcon = styled.div`
  cursor: grab;
  margin-right: 6px;
`;

const StyledOptionMenuIcon = styled.div`
  svg {
    font-size: 14px;
  }
`;

const StyledOptionTrashIcon = styled.div`
  margin-left: 6px;
  svg {
    font-size: 14px;
  }
`;

const StyledDescription = styled.div`
  width: 100%;
  font-size: 12px;
  margin: 2px 0px 0px 3px;
  color: ${(props) => props.theme.fontColorSecondary};
`;

const OptionSettings = ({
  id,
  activeFieldPosition, //Active field object. Used for drag and drop
  label,
  type,
  displayType,
  options,
  config,
  onCreate,
  onDelete,
  onChange,
  targetValue
}) => {

  const theme = useContext(ThemeContext);

  const [displayOptionsModal, setDisplayOptionsModal] = useState(null);
  const [activeOption, setActiveOption] = useState(null);

  const isSelectorType = displayType && (displayType !== FieldDisplayTypes.original && displayType !== FieldDisplayTypes.horizontal);

  const validOptions = options.filter((option) => !option.deleted);

  const optionNodes = validOptions.map((option) => (
    <Option
      key={option._id}
      {...{
        id,
        activeFieldPosition,
        type,
        displayType,
        option,
        onChange,
        onDelete,
      }}
      active={!!(isSelectorType && targetValue && option._id === targetValue)}
      onEdit={(id) => {
        const option = validOptions.find((option) => option._id === id);
        setActiveOption(option);
        setDisplayOptionsModal(true);

      }}
    />
  ));

  return (
    <StyledWrapper>
      {!label ? null : (
        <StyledLabelWrapper>
          <StyledLabel>{label}</StyledLabel>
        </StyledLabelWrapper>
      )}
      <StyledOptionsWrapper theme={theme}>
        {optionNodes}
        <StyledAddWrapper>
          <OptionSettingsModal
            config={config}
            display={displayOptionsModal}
            hideWidth={type !== FieldTypes.multiSelect}
            options={options}
            option={activeOption}
            onHide={() => {
              setActiveOption(null);
              setDisplayOptionsModal(false);
            }}
            onSubmit={(optionId, option) => {
              if (activeOption && activeOption._id) onChange(optionId, option);
              else onCreate(option);
            }}
          >
            <div>
              <Button
                styles={{ width: '100%', padding: '6px' }}
                icon={ThemeIcons.settings.optionAdd}
                onClick={() => setDisplayOptionsModal(true)}
              />
            </div>
          </OptionSettingsModal>
        </StyledAddWrapper>
      </StyledOptionsWrapper>
      {!isSelectorType ? null : (
        <StyledDescription theme={theme} data-testid={`${id}-option-settings-description`}>
          Drag options onto the page using the drag icon.
        </StyledDescription>
      )}
    </StyledWrapper>
  );
};

OptionSettings.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  type: PropTypes.string,
  displayType: PropTypes.string,
  options: PropTypes.array,
  onChange: PropTypes.func,
  onCreate: PropTypes.func,
  onDelete: PropTypes.func
};

const Option = ({
  id,
  activeFieldPosition, //Active field object. Used for drag and drop
  type,
  displayType,
  option,
  onDelete,
  onEdit,
  active
}) => {

  const theme = useContext(ThemeContext);

  const [height, setHeight] = useState(null);

  const isSelectorType = displayType && displayType !== FieldDisplayTypes.original;

  /**
   * Important Note:
   *
   * fieldPosition.field Explanation
   *
   * We need the fieldPosition.field for dropdowns, multi-selects, type fields ( really any kind of field
   * that has options on it). Because there can be many fieldPostions for a single field, we link the
   * all the associated fieldPostions back to the original field using the `fieldPosition.field` property.
   *
   * How can multiple fieldPositions be associated with a single field?
   *
   * When there are many fieldPositions for the same field they are each pointing at different individual
   * options in the "field.options" array. The target option that the field is pointing at is represented by
   * fieldPosition.targetValue property.
   */
  const optionFieldPosition = {
    ...activeFieldPosition,
    type,
    displayType,
    targetValue: option._id,
  };

  if (isSelectorType) {
    optionFieldPosition.width = 40;
    optionFieldPosition.height = 40;
  }

  const [collectedProps, drag] = useDrag({ type, item: optionFieldPosition });

  return (
    <StyledOption
      theme={theme}
      key={option._id}
      ref={drag}
      {...collectedProps}
      isSelectorType={isSelectorType}
      data-testid={`${id}-option-settings-${option._id}`}
      active={active}
    >
      {!isSelectorType ? null : (
        <StyledOptionDragIcon>
          <Icon
            id={`${id}-option-settings-${option._id}-drag`}
            icon={active ? ThemeIcons.settings.targetValue : ThemeIcons.misc.drag}
            size="sm"
            color={active ? theme.primaryColor : theme.borderColor}
            hoverColor={theme.primaryColor}
            hoverCursor="grab"
          />
        </StyledOptionDragIcon>
      )}
      <StyledOptionLabel
        ref={(e) => {
          if (e && typeof height !== 'number') setHeight(e.offsetHeight);
        }}
      >
        {option.value}
      </StyledOptionLabel>
      <StyledOptionMenuIcon>
        <Icon
          id={`${id}-option-settings-${option._id}-edit`}
          icon={ThemeIcons.settings.edit}
          size="sm"
          color={theme.borderColor}
          hoverColor={theme.primaryColor}
          onClick={() => {
            onEdit(option._id);
          }}
        />
      </StyledOptionMenuIcon>
      <StyledOptionTrashIcon>
        <Icon
          id={`${id}-option-settings-${option._id}-delete`}
          icon={ThemeIcons.settings.delete}
          size="sm"
          color={theme.borderColor}
          hoverColor={theme.dangerColor}
          onClick={() => { if (onDelete) onDelete(option._id); }}
        />
      </StyledOptionTrashIcon>
    </StyledOption>
  );

};

Option.propTypes = {
  id: PropTypes.string,
  fieldId: PropTypes.string,
  type: PropTypes.string,
  displayType: PropTypes.string,
  option: PropTypes.object,
  onChange: PropTypes.func,
  onDelete: PropTypes.func
};

export default OptionSettings;
